/**
 * Shared Device Work Router
 *
 * Directs mutations originating from Field floor work on a shared device
 * to the shared-device outbox when an active offline session context is set.
 *
 * When no context is set, caller fallbacks to standard per-user outbox.
 */

import type {
  ChecklistCommentPayload,
  ChecklistProofPayload,
  ChecklistTogglePayload,
  ChecklistVoiceMemoPayload,
  HelperMutationRecord,
  HelperMutationType,
  StockCountRoundPayload,
  StockScanSuggestionPayload,
  TimeClockPayload,
  OpsDueRecordPayload,
  OpsCheckpointScanPayload,
  OpsSkipDuePayload,
  OpsRaiseRequestPayload,
  OpsAttendanceRollPayload,
  OpsStartRequestPayload,
  OpsMarkRequestDonePayload,
  OpsBmwSavePayload,
  OpsDprSavePayload,
  OpsConIssueCreatePayload,
  OpsDprLineBlob,
} from './helper/helperOutboxDb'
import {
  enqueueSharedDeviceItem,
  storeSharedDeviceBlob,
} from './sharedDeviceOutbox'
import type {
  SharedDeviceChecklistCommentPayload,
  SharedDeviceChecklistProofPayload,
  SharedDeviceChecklistTogglePayload,
  SharedDeviceChecklistVoiceMemoPayload,
  SharedDeviceStockCountRoundPayload,
  SharedDeviceStockScanPayload,
  SharedDeviceTimeClockPayload,
} from '../types/ops'

export interface SharedDeviceWorkRouterContext {
  deviceId: string
  personUid: string
  offlineSessionId: string
}

let activeContext: SharedDeviceWorkRouterContext | null = null

export function setSharedDeviceWorkRouterContext(ctx: SharedDeviceWorkRouterContext): void {
  activeContext = { ...ctx }
}

export function getSharedDeviceWorkRouterContext(): SharedDeviceWorkRouterContext | null {
  return activeContext ? { ...activeContext } : null
}

export function clearSharedDeviceWorkRouterContext(): void {
  activeContext = null
}

export function generateRouterOpId(prefix: string = 'sd-op'): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`
  }
  const rand = Math.random().toString(36).slice(2, 10)
  return `${prefix}-${Date.now()}-${rand}`
}

function makeSyntheticRecord(
  id: string,
  type: HelperMutationType,
  payload: HelperMutationRecord['payload'],
  at: number,
): HelperMutationRecord {
  return {
    id,
    type,
    payload,
    status: 'PENDING',
    retryCount: 0,
    error: null,
    createdAt: at,
    lastUpdatedClient: at,
  }
}

export async function routeChecklistToggle(
  data: ChecklistTogglePayload & { itemId?: string; at?: number },
  ctx: SharedDeviceWorkRouterContext,
  actionTime?: number,
): Promise<HelperMutationRecord> {
  const at = actionTime ?? data.at ?? Date.now()
  const opId = generateRouterOpId('chk-toggle')
  const checklistItemId = data.checklistItemId ?? data.itemId
  const payload: SharedDeviceChecklistTogglePayload = {
    checklistItemId,
    isCompleted: data.isCompleted,
    workflowStatus: data.workflowStatus,
    pageKey: data.pageKey ?? null,
    assignedAt: data.assignedAt ?? null,
  }

  await enqueueSharedDeviceItem(ctx.deviceId, {
    opId,
    personUid: ctx.personUid,
    offlineSessionId: ctx.offlineSessionId,
    at,
    kind: 'CHECKLIST_TOGGLE',
    payload: payload as unknown as Record<string, unknown>,
  })

  return makeSyntheticRecord(opId, 'CHECKLIST_TOGGLE', data, at)
}

export async function routeChecklistComment(
  data: ChecklistCommentPayload & { itemId?: string; text?: string; at?: number },
  ctx: SharedDeviceWorkRouterContext,
  actionTime?: number,
): Promise<HelperMutationRecord> {
  const at = actionTime ?? data.at ?? Date.now()
  const opId = generateRouterOpId('chk-comment')
  const checklistItemId = data.checklistItemId ?? data.itemId
  const text = data.text ?? data.body
  const payload: SharedDeviceChecklistCommentPayload = {
    checklistItemId,
    text,
    body: data.body ?? text,
    pageKey: data.pageKey ?? null,
    assignedAt: data.assignedAt ?? null,
  }

  await enqueueSharedDeviceItem(ctx.deviceId, {
    opId,
    personUid: ctx.personUid,
    offlineSessionId: ctx.offlineSessionId,
    at,
    kind: 'CHECKLIST_COMMENT',
    payload: payload as unknown as Record<string, unknown>,
  })

  return makeSyntheticRecord(opId, 'CHECKLIST_COMMENT', data, at)
}

export async function routeChecklistProof(
  data: Omit<ChecklistProofPayload, 'blobKey'> & {
    itemId?: string
    workflowStatus?: SharedDeviceChecklistProofPayload['workflowStatus']
    at?: number
  },
  file: Blob,
  ctx: SharedDeviceWorkRouterContext,
  actionTime?: number,
): Promise<HelperMutationRecord> {
  const at = actionTime ?? data.at ?? Date.now()
  const opId = generateRouterOpId('chk-proof')
  const blobKey = generateRouterOpId('blob-proof')

  await storeSharedDeviceBlob(ctx.deviceId, {
    blobKey,
    blob: file,
    contentType: data.contentType,
    size: file.size,
    createdAt: at,
  })

  const checklistItemId = data.checklistItemId ?? data.itemId
  const payload: SharedDeviceChecklistProofPayload = {
    checklistItemId,
    fileId: '',
    pageKey: data.pageKey ?? null,
    assignedAt: data.assignedAt ?? null,
    workflowStatus: data.workflowStatus,
  }

  await enqueueSharedDeviceItem(ctx.deviceId, {
    opId,
    personUid: ctx.personUid,
    offlineSessionId: ctx.offlineSessionId,
    at,
    kind: 'CHECKLIST_PROOF',
    payload: payload as unknown as Record<string, unknown>,
    blobKey,
  })

  return makeSyntheticRecord(opId, 'CHECKLIST_PROOF_UPLOAD', { ...data, blobKey }, at)
}

export async function routeChecklistVoiceMemo(
  data: Omit<ChecklistVoiceMemoPayload, 'blobKey'> & { itemId?: string; at?: number },
  audio: Blob,
  ctx: SharedDeviceWorkRouterContext,
  actionTime?: number,
): Promise<HelperMutationRecord> {
  const at = actionTime ?? data.at ?? Date.now()
  const opId = generateRouterOpId('chk-voice')
  const blobKey = generateRouterOpId('blob-voice')

  await storeSharedDeviceBlob(ctx.deviceId, {
    blobKey,
    blob: audio,
    contentType: 'audio/webm',
    size: audio.size,
    createdAt: at,
  })

  const checklistItemId = data.checklistItemId ?? data.itemId
  const payload: SharedDeviceChecklistVoiceMemoPayload = {
    checklistItemId,
    fileId: '',
    durationMs: data.durationMs,
    pageKey: data.pageKey ?? null,
    assignedAt: data.assignedAt ?? null,
  }

  await enqueueSharedDeviceItem(ctx.deviceId, {
    opId,
    personUid: ctx.personUid,
    offlineSessionId: ctx.offlineSessionId,
    at,
    kind: 'CHECKLIST_VOICE_MEMO',
    payload: payload as unknown as Record<string, unknown>,
    blobKey,
  })

  return makeSyntheticRecord(opId, 'CHECKLIST_VOICE_MEMO', { ...data, blobKey }, at)
}

export async function routeStockScanSuggestion(
  data: StockScanSuggestionPayload & { at?: number },
  ctx: SharedDeviceWorkRouterContext,
  actionTime?: number,
): Promise<HelperMutationRecord> {
  const at = actionTime ?? data.at ?? data.createdAtClient ?? Date.now()
  const opId = generateRouterOpId('stk-scan')

  const payload: SharedDeviceStockScanPayload = {
    stockItemId: data.stockItemId,
    kind: data.kind,
    barcode: data.barcode ?? null,
    direction: data.direction,
    quantity: data.quantity,
    countedQuantity: data.countedQuantity ?? data.quantity ?? 0,
    systemQuantityAtScan: data.systemQuantityAtScan ?? 0,
    note: data.note ?? null,
    locationId: data.locationId ?? null,
    transferId: data.transferId ?? null,
  }

  await enqueueSharedDeviceItem(ctx.deviceId, {
    opId,
    personUid: ctx.personUid,
    offlineSessionId: ctx.offlineSessionId,
    at,
    kind: 'STOCK_SCAN_SUGGESTION',
    payload: payload as unknown as Record<string, unknown>,
  })

  return makeSyntheticRecord(opId, 'STOCK_SCAN_SUGGESTION', data, at)
}

export async function routeStockCountRound(
  data: StockCountRoundPayload & { at?: number },
  ctx: SharedDeviceWorkRouterContext,
  actionTime?: number,
): Promise<HelperMutationRecord> {
  const at = actionTime ?? data.at ?? Date.now()
  const opId = generateRouterOpId('stk-round')

  const payload: SharedDeviceStockCountRoundPayload = {
    roundId: data.roundId,
    suggestions: data.suggestions.map((s) => ({
      stockItemId: s.stockItemId ?? '',
      countedQuantity: s.countedQuantity,
      systemQuantityAtScan: s.systemQuantityAtScan,
      sku: s.sku,
      barcode: s.barcode ?? null,
      note: s.note ?? null,
    })),
  }

  await enqueueSharedDeviceItem(ctx.deviceId, {
    opId,
    personUid: ctx.personUid,
    offlineSessionId: ctx.offlineSessionId,
    at,
    kind: 'STOCK_COUNT_ROUND',
    payload: payload as unknown as Record<string, unknown>,
  })

  return makeSyntheticRecord(opId, 'STOCK_COUNT_ROUND', data, at)
}

export async function routeTimeClockMutation(
  data: TimeClockPayload & { note?: string | null; billable?: boolean; at?: number },
  ctx: SharedDeviceWorkRouterContext,
  actionTime?: number,
): Promise<HelperMutationRecord> {
  const at = actionTime ?? data.at ?? (data.occurredAt ? new Date(data.occurredAt).getTime() : Date.now())
  const opId = data.clientGeneratedId || generateRouterOpId('time-clock')

  const payload: SharedDeviceTimeClockPayload = {
    action: data.action,
    kind: data.kind ?? 'CLOCK',
    clientGeneratedId: data.clientGeneratedId,
    nodeId: data.nodeId ?? null,
    geo: data.geo ?? null,
    note: data.note ?? null,
    billable: data.billable ?? false,
  }

  await enqueueSharedDeviceItem(ctx.deviceId, {
    opId,
    personUid: ctx.personUid,
    offlineSessionId: ctx.offlineSessionId,
    at,
    kind: 'TIME_CLOCK',
    payload: payload as unknown as Record<string, unknown>,
  })

  return makeSyntheticRecord(opId, 'TIME_CLOCK', data, at)
}

export async function routeRegisterRecord(
  data: OpsDueRecordPayload & {
    at?: number
    blobKey?: string
    photoBindings?: Array<{ fieldKey: string; blobKey: string }>
  },
  ctx: SharedDeviceWorkRouterContext,
  actionTime?: number,
): Promise<HelperMutationRecord> {
  const at = actionTime ?? data.at ?? Date.now()
  const opId = data.opId || data.clientMutationId || generateRouterOpId('ops-record')
  const blobKey = data.blobKey || data.photoBindings?.[0]?.blobKey
  const photoBindings = data.photoBindings || (blobKey ? [{ fieldKey: 'photo', blobKey }] : undefined)

  await enqueueSharedDeviceItem(ctx.deviceId, {
    opId,
    personUid: ctx.personUid,
    offlineSessionId: ctx.offlineSessionId,
    at,
    kind: 'REGISTER_RECORD',
    payload: { ...data, opId, ...(photoBindings ? { photoBindings } : {}) } as unknown as Record<string, unknown>,
    blobKey,
    photoBindings,
  })

  return makeSyntheticRecord(opId, 'OPS_DUE_RECORD', data, at)
}

export async function routeAttendanceCheckIn(
  data: Record<string, unknown> & { at?: number; opId?: string; clientMutationId?: string },
  ctx: SharedDeviceWorkRouterContext,
  actionTime?: number,
): Promise<HelperMutationRecord> {
  const at = actionTime ?? data.at ?? Date.now()
  const opId = data.opId || data.clientMutationId || generateRouterOpId('att-checkin')

  await enqueueSharedDeviceItem(ctx.deviceId, {
    opId,
    personUid: ctx.personUid,
    offlineSessionId: ctx.offlineSessionId,
    at,
    kind: 'ATTENDANCE_CHECK_IN',
    payload: { ...data, opId },
  })

  return makeSyntheticRecord(
    opId,
    'OPS_ATTENDANCE_ROLL',
    data as unknown as HelperMutationRecord['payload'],
    at,
  )
}

export async function routeCheckpointScan(
  data: OpsCheckpointScanPayload & { at?: number },
  ctx: SharedDeviceWorkRouterContext,
  actionTime?: number,
): Promise<HelperMutationRecord> {
  const at = actionTime ?? data.at ?? Date.now()
  const opId = data.opId || data.clientMutationId || generateRouterOpId('ops-scan')

  await enqueueSharedDeviceItem(ctx.deviceId, {
    opId,
    personUid: ctx.personUid,
    offlineSessionId: ctx.offlineSessionId,
    at,
    kind: 'CHECKPOINT_SCAN',
    payload: { ...data, opId } as unknown as Record<string, unknown>,
  })

  return makeSyntheticRecord(opId, 'OPS_CHECKPOINT_SCAN', data, at)
}

export async function routeSkipDue(
  data: OpsSkipDuePayload & { at?: number },
  ctx: SharedDeviceWorkRouterContext,
  actionTime?: number,
): Promise<HelperMutationRecord> {
  const at = actionTime ?? data.at ?? Date.now()
  const opId = data.opId || data.clientMutationId || generateRouterOpId('ops-skip')

  await enqueueSharedDeviceItem(ctx.deviceId, {
    opId,
    personUid: ctx.personUid,
    offlineSessionId: ctx.offlineSessionId,
    at,
    kind: 'SKIP_DUE',
    payload: { ...data, opId } as unknown as Record<string, unknown>,
  })

  return makeSyntheticRecord(opId, 'OPS_SKIP_DUE', data, at)
}

export async function routeRaiseRequest(
  data: OpsRaiseRequestPayload & { at?: number },
  ctx: SharedDeviceWorkRouterContext,
  actionTime?: number,
): Promise<HelperMutationRecord> {
  const at = actionTime ?? data.at ?? Date.now()
  const opId = data.opId || data.clientMutationId || generateRouterOpId('ops-req')

  await enqueueSharedDeviceItem(ctx.deviceId, {
    opId,
    personUid: ctx.personUid,
    offlineSessionId: ctx.offlineSessionId,
    at,
    kind: 'RAISE_REQUEST',
    payload: { ...data, opId } as unknown as Record<string, unknown>,
  })

  return makeSyntheticRecord(opId, 'OPS_RAISE_REQUEST', data, at)
}

export async function routeAttendanceRoll(
  data: OpsAttendanceRollPayload & { at?: number },
  ctx: SharedDeviceWorkRouterContext,
  actionTime?: number,
): Promise<HelperMutationRecord> {
  const at = actionTime ?? data.at ?? Date.now()
  const opId = data.opId || data.clientMutationId || generateRouterOpId('ops-roll')

  await enqueueSharedDeviceItem(ctx.deviceId, {
    opId,
    personUid: ctx.personUid,
    offlineSessionId: ctx.offlineSessionId,
    at,
    kind: 'ATTENDANCE_ROLL',
    payload: { ...data, opId } as unknown as Record<string, unknown>,
  })

  return makeSyntheticRecord(opId, 'OPS_ATTENDANCE_ROLL', data, at)
}

export async function routeStartRequest(
  data: OpsStartRequestPayload & { at?: number },
  ctx: SharedDeviceWorkRouterContext,
  actionTime?: number,
): Promise<HelperMutationRecord> {
  const at = actionTime ?? data.at ?? Date.now()
  const opId = data.opId || data.clientMutationId || generateRouterOpId('ops-req-start')

  await enqueueSharedDeviceItem(ctx.deviceId, {
    opId,
    personUid: ctx.personUid,
    offlineSessionId: ctx.offlineSessionId,
    at,
    kind: 'START_REQUEST',
    payload: { ...data, opId } as unknown as Record<string, unknown>,
  })

  return makeSyntheticRecord(opId, 'OPS_START_REQUEST', data, at)
}

export async function routeMarkRequestDone(
  data: OpsMarkRequestDonePayload & { at?: number },
  ctx: SharedDeviceWorkRouterContext,
  fileOrFiles?: Blob | Blob[] | File | File[],
  actionTime?: number,
): Promise<HelperMutationRecord> {
  const at = actionTime ?? data.at ?? Date.now()
  const opId = data.opId || data.clientMutationId || generateRouterOpId('ops-req-done')
  let blobKey: string | undefined = data.blobKey
  const blobKeys: string[] = data.blobKeys ? [...data.blobKeys] : []

  const files = fileOrFiles
    ? Array.isArray(fileOrFiles)
      ? fileOrFiles
      : [fileOrFiles]
    : []

  for (const f of files) {
    const k = generateRouterOpId('blob-req-done')
    await storeSharedDeviceBlob(ctx.deviceId, {
      blobKey: k,
      blob: f,
      contentType: f.type || 'image/jpeg',
      size: f.size,
      createdAt: at,
    })
    blobKeys.push(k)
  }

  if (!blobKey && blobKeys.length > 0) {
    blobKey = blobKeys[0]
  }

  const payload: Record<string, unknown> = {
    ...data,
    opId,
    ...(blobKey ? { blobKey } : {}),
    ...(blobKeys.length > 0 ? { blobKeys } : {}),
  }

  await enqueueSharedDeviceItem(ctx.deviceId, {
    opId,
    personUid: ctx.personUid,
    offlineSessionId: ctx.offlineSessionId,
    at,
    kind: 'MARK_REQUEST_DONE',
    payload,
    ...(blobKey ? { blobKey } : {}),
    ...(blobKeys.length > 0 ? { blobKeys } : {}),
  })

  return makeSyntheticRecord(
    opId,
    'OPS_MARK_REQUEST_DONE',
    {
      ...data,
      ...(blobKey ? { blobKey } : {}),
      ...(blobKeys.length > 0 ? { blobKeys } : {}),
    },
    at,
  )
}

export async function routeBmwSave(
  data: OpsBmwSavePayload & { at?: number },
  ctx: SharedDeviceWorkRouterContext,
  actionTime?: number,
): Promise<HelperMutationRecord> {
  const at = actionTime ?? data.at ?? Date.now()
  const opId = data.opId || data.clientMutationId || generateRouterOpId('ops-bmw-save')

  await enqueueSharedDeviceItem(ctx.deviceId, {
    opId,
    personUid: ctx.personUid,
    offlineSessionId: ctx.offlineSessionId,
    at,
    kind: 'BMW_SAVE',
    payload: { ...data, opId } as unknown as Record<string, unknown>,
  })

  return makeSyntheticRecord(opId, 'OPS_BMW_SAVE', data, at)
}

export async function routeDprSave(
  data: (OpsDprSavePayload | { workspaceId: string; projectId: string; reportDate: string; shift: string; lines: unknown[]; clientMutationId?: string; opId?: string }) & { at?: number },
  ctx: SharedDeviceWorkRouterContext,
  lineBlobs?: OpsDprLineBlob[],
  lineFiles?: Array<{ lineId: string; file: Blob | File }>,
  actionTime?: number,
): Promise<HelperMutationRecord> {
  const at = actionTime ?? data.at ?? Date.now()
  const opId = data.opId || data.clientMutationId || generateRouterOpId('ops-dpr-save')

  const blobsToStore: OpsDprLineBlob[] = lineBlobs ? [...lineBlobs] : []
  if (lineFiles && lineFiles.length > 0) {
    for (const lf of lineFiles) {
      const k = generateRouterOpId('blob-dpr-line')
      await storeSharedDeviceBlob(ctx.deviceId, {
        blobKey: k,
        blob: lf.file,
        contentType: lf.file.type || 'image/jpeg',
        size: lf.file.size,
        createdAt: at,
      })
      blobsToStore.push({ lineId: lf.lineId, blobKey: k, blobKeys: [k] })
    }
  }

  const payload: Record<string, unknown> = {
    ...data,
    opId,
    ...(blobsToStore.length > 0 ? { lineBlobs: blobsToStore } : {}),
  }

  await enqueueSharedDeviceItem(ctx.deviceId, {
    opId,
    personUid: ctx.personUid,
    offlineSessionId: ctx.offlineSessionId,
    at,
    kind: 'DPR_SAVE',
    payload,
  })

  return makeSyntheticRecord(
    opId,
    'OPS_DPR_SAVE',
    { ...data, clientMutationId: opId, ...(blobsToStore.length > 0 ? { lineBlobs: blobsToStore } : {}) } as unknown as HelperMutationRecord['payload'],
    at,
  )
}

export async function routeConIssueCreate(
  data: OpsConIssueCreatePayload & { at?: number },
  ctx: SharedDeviceWorkRouterContext,
  fileOrFiles?: Blob | Blob[] | File | File[],
  actionTime?: number,
): Promise<HelperMutationRecord> {
  const at = actionTime ?? data.at ?? Date.now()
  const opId = data.opId || data.clientMutationId || generateRouterOpId('ops-con-issue')
  const blobKeys: string[] = data.blobKeys ? [...data.blobKeys] : []

  const files = fileOrFiles
    ? Array.isArray(fileOrFiles)
      ? fileOrFiles
      : [fileOrFiles]
    : []

  for (const f of files) {
    const k = generateRouterOpId('blob-con-issue')
    await storeSharedDeviceBlob(ctx.deviceId, {
      blobKey: k,
      blob: f,
      contentType: f.type || 'image/jpeg',
      size: f.size,
      createdAt: at,
    })
    blobKeys.push(k)
  }

  const payload: Record<string, unknown> = {
    ...data,
    opId,
    ...(blobKeys.length > 0 ? { blobKeys } : {}),
  }

  await enqueueSharedDeviceItem(ctx.deviceId, {
    opId,
    personUid: ctx.personUid,
    offlineSessionId: ctx.offlineSessionId,
    at,
    kind: 'CON_ISSUE_CREATE',
    payload,
    ...(blobKeys.length > 0 ? { blobKeys } : {}),
  })

  return makeSyntheticRecord(
    opId,
    'OPS_CON_ISSUE_CREATE',
    { ...data, clientMutationId: opId, ...(blobKeys.length > 0 ? { blobKeys } : {}) },
    at,
  )
}

